import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Image,
  ImageSourcePropType,
  Platform,
} from 'react-native';
import PlatformIcon from '../common/PlatformIcon';

// 直接定义颜色，避免 @cubemeet/core 导入问题
const MODULE_COLOR = '#F97316'; // travel 模块的橙色

// ============ 类型定义 ============

export interface PrivateSettlementItem {
  /** 支付方用户ID */
  fromUserId: string;
  /** 支付方名称 */
  fromName: string;
  /** 支付方头像 */
  fromAvatarUrl?: string | null;
  /** 接收方用户ID */
  toUserId: string;
  /** 接收方名称 */
  toName: string;
  /** 接收方头像 */
  toAvatarUrl?: string | null;
  /** 结算金额 */
  amount: number;
}

export interface PrivateSettlementSectionProps {
  /** 结算列表 */
  settlements: PrivateSettlementItem[];
  /** 当前用户ID (用于高亮显示"你") */
  currentUserId?: string | null;
  /** 货币符号 */
  currency?: string;
  /** 格式化金额 */
  formatCurrency?: (amount: number, currency?: string) => string;
  /** 获取头像 source */
  getAvatarSource?: (url: string) => ImageSourcePropType | null;
}

// ============ 默认格式化函数 ============

const defaultFormatCurrency = (amount: number, currency?: string): string => {
  const symbol = currency || 'HK$';
  return `${symbol}${amount.toFixed(2)}`;
};

// ============ 组件 ============

const PrivateSettlementSection: React.FC<PrivateSettlementSectionProps> = ({
  settlements,
  currentUserId,
  currency = 'HK$',
  formatCurrency = defaultFormatCurrency,
  getAvatarSource,
}) => {
  const renderAvatar = (avatarUrl?: string | null, name?: string) => {
    const initial = (name || 'U').charAt(0).toUpperCase();

    if (avatarUrl && getAvatarSource) {
      const source = getAvatarSource(avatarUrl);
      if (source) {
        return <Image source={source} style={styles.avatar} />;
      }
    }

    return (
      <View style={[styles.avatar, styles.avatarPlaceholder]}>
        <Text style={styles.avatarPlaceholderText}>{initial}</Text>
      </View>
    );
  };

  // 空状态
  if (settlements.length === 0) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <PlatformIcon name="receipt" size={20} color={MODULE_COLOR} family="MaterialIcons" />
          <Text style={styles.title}>私账往来</Text>
        </View>
        <View style={styles.emptyState}>
          <PlatformIcon name="handshake" size={48} color="#D1D5DB" family="MaterialIcons" />
          <Text style={styles.emptyText}>当前没有成员之间的往来记录</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* 标题 */}
      <View style={styles.header}>
        <PlatformIcon name="receipt" size={20} color={MODULE_COLOR} family="MaterialIcons" />
        <Text style={styles.title}>私账往来</Text>
      </View>

      {/* 结算列表 */}
      {settlements.map((settlement, index) => {
        const isCurrentUserFrom = currentUserId === settlement.fromUserId;
        const isCurrentUserTo = currentUserId === settlement.toUserId;
        
        const fromLabel = isCurrentUserFrom ? '你' : settlement.fromName;
        const toLabel = isCurrentUserTo ? '你' : settlement.toName;

        // 生成描述文字
        let description = `${fromLabel} 应付 ${toLabel} ${formatCurrency(settlement.amount, currency)}`;
        if (isCurrentUserTo) {
          description = `${fromLabel} 欠你 ${formatCurrency(settlement.amount, currency)}`;
        } else if (isCurrentUserFrom) {
          description = `你欠 ${toLabel} ${formatCurrency(settlement.amount, currency)}`;
        }

        return (
          <View key={`${settlement.fromUserId}-${settlement.toUserId}-${index}`} style={styles.settlementCard}>
            {/* 上部：头像 -> 箭头 -> 头像 */}
            <View style={styles.settlementRow}>
              {/* 支付方 */}
              <View style={styles.participantLeft}>
                {renderAvatar(settlement.fromAvatarUrl, settlement.fromName)}
                <Text 
                  style={[
                    styles.participantName, 
                    isCurrentUserFrom && styles.participantNameHighlight
                  ]} 
                  numberOfLines={1}
                >
                  {fromLabel}
                </Text>
              </View>

              {/* 箭头 */}
              <View style={styles.arrowContainer}>
                <View style={styles.arrowCircle}>
                  <PlatformIcon name="chevron-right" size={18} color="#64748B" family="MaterialIcons" />
                </View>
              </View>

              {/* 接收方 */}
              <View style={styles.participantRight}>
                {renderAvatar(settlement.toAvatarUrl, settlement.toName)}
                <Text 
                  style={[
                    styles.participantName, 
                    isCurrentUserTo && styles.participantNameHighlight
                  ]} 
                  numberOfLines={1}
                >
                  {toLabel}
                </Text>
              </View>
            </View>

            {/* 下部：描述文字 */}
            <Text style={styles.settlementDescription}>{description}</Text>
          </View>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    ...Platform.select({
      web: {
        boxShadow: '0px 2px 8px rgba(0,0,0,0.06)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 8,
        elevation: 2,
      },
    }),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    gap: 8,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 32,
  },
  emptyText: {
    marginTop: 12,
    fontSize: 14,
    color: '#9CA3AF',
    textAlign: 'center',
  },
  settlementCard: {
    backgroundColor: '#F8FAFC',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
  },
  settlementRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  participantLeft: {
    flex: 1,
    alignItems: 'flex-start',
  },
  participantRight: {
    flex: 1,
    alignItems: 'flex-end',
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    marginBottom: 6,
  },
  avatarPlaceholder: {
    backgroundColor: MODULE_COLOR,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarPlaceholderText: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '600',
  },
  participantName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
  },
  participantNameHighlight: {
    color: MODULE_COLOR,
  },
  arrowContainer: {
    paddingHorizontal: 16,
  },
  arrowCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    alignItems: 'center',
    justifyContent: 'center',
  },
  settlementDescription: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
  },
});

export default PrivateSettlementSection;
